import {
  useLayoutEffect,
  type ChangeEvent,
  type KeyboardEvent,
  type ReactElement,
  type RefObject,
  type SyntheticEvent,
} from "react";
import { useSubmitShortcut } from "@primitives/index";
import { findMentionAtCaret, type MentionRange } from "./mention";
import styles from "./QuestionField.module.css";

/**
 * QuestionField: the auto-growing textarea.
 *
 * Reports the `@` token under the caret on every change and caret move.
 * Cmd/Ctrl+Enter sends. While a mention list is open, the arrow keys, Enter,
 * Tab and Escape drive that list instead of the text.
 */

export interface QuestionFieldProps {
  readonly id: string;
  readonly textareaRef: RefObject<HTMLTextAreaElement>;
  readonly listId: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onMentionChange: (mention: MentionRange | null) => void;
  readonly mentionOpen: boolean;
  readonly activeOptionId: string | undefined;
  readonly onMoveHighlight: (delta: number) => void;
  readonly onPickHighlighted: () => boolean;
  readonly onDismissMention: () => void;
  readonly onSend: () => void;
  readonly describedBy: string | undefined;
}

export function QuestionField({
  id,
  textareaRef,
  listId,
  value,
  onChange,
  onMentionChange,
  mentionOpen,
  activeOptionId,
  onMoveHighlight,
  onPickHighlighted,
  onDismissMention,
  onSend,
  describedBy,
}: QuestionFieldProps): ReactElement {
  // Height follows content. Measured before paint, so no frame shows the
  // old height with the new text.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (el === null) return;
    el.style.height = "0px";
    el.style.height = `${String(el.scrollHeight)}px`;
  }, [textareaRef, value]);

  const syncMention = (el: HTMLTextAreaElement): void => {
    onMentionChange(findMentionAtCaret(el.value, el.selectionStart));
  };

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    onChange(event.target.value);
    syncMention(event.target);
  };

  const handleSelect = (event: SyntheticEvent<HTMLTextAreaElement>): void => {
    syncMention(event.currentTarget);
  };

  // Cmd/Ctrl+Enter submits the enclosing form (or calls `onSend` without one).
  const submitShortcut = useSubmitShortcut(onSend);

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    submitShortcut(event);
    if (event.isDefaultPrevented()) return;
    if (!mentionOpen) return;
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        onMoveHighlight(1);
        return;
      case "ArrowUp":
        event.preventDefault();
        onMoveHighlight(-1);
        return;
      case "Enter":
      case "Tab":
        if (onPickHighlighted()) event.preventDefault();
        return;
      case "Escape":
        // Consumed here so the dialog stays open.
        event.preventDefault();
        onDismissMention();
        return;
      default:
        return;
    }
  };

  return (
    <textarea
      id={id}
      ref={textareaRef}
      className={styles.textarea}
      data-focus-style="custom"
      placeholder={"Ask a question\u2026"}
      aria-label="Question"
      aria-describedby={describedBy}
      aria-controls={mentionOpen ? listId : undefined}
      aria-activedescendant={mentionOpen ? activeOptionId : undefined}
      value={value}
      rows={5}
      spellCheck
      onChange={handleChange}
      onSelect={handleSelect}
      onKeyDown={handleKeyDown}
      onBlur={() => {
        if (mentionOpen) onDismissMention();
      }}
    />
  );
}
